import { Table } from '@amzn/awsui-components-core';
import {
  WorkflowEvent,
  WorkflowEventTypes,
  ContentAccessUser,
} from '@amzn/awscat-pattern-library-service-model';
import { useCollection } from '@amzn/awsui-collection-hooks';
import arraySort from 'array-sort';
import { DateTime } from 'luxon';
import React, { useContext, useEffect, useState } from 'react';
import { MplServiceClientFactory } from '../../api/MplServiceClientFactory';
import { displayAuthorInfo, getUserTitleFromProfile } from '../../api/UtilsApi';
import LoadingIndicator from '../common/LoadingIndicator';
import {
  contentAudienceDisplayNames,
  userTitleDisplayNames,
  workflowEventTypeDisplayNames,
} from '../utils/DisplayNames';
import { ContentViewerContext } from './ContentViewerContext';
interface Props {
  contentId: string;
  workflowId: number;
  coAuthors?: ContentAccessUser[];
  authorId?: string;
}

const mplService = MplServiceClientFactory.create();

const WorkflowHistoryEvents = (props: Props) => {
  const { contentId, workflowId } = props;
  const [isLoading, setIsLoading] = useState(false);
  const [events, setEvents] = useState<WorkflowEvent[]>([]);
  const viewerCtx = useContext(ContentViewerContext);
  const { coAuthors, peerReviewers, owner } = viewerCtx;

  const { items, collectionProps } = useCollection(events, {
    sorting: {},
    selection: {},
  });

  useEffect(() => {
    const fetchEvents = async () => {
      setIsLoading(true);
      const { events: wfEvents } = await mplService.getContentEvents(contentId, workflowId);
      setEvents(wfEvents);
      setIsLoading(false);
    };

    fetchEvents();
  }, [contentId, workflowId]);

  const displayInfo = (item: WorkflowEvent) => {
    const { type, data } = item;
    switch (type) {
      case WorkflowEventTypes.DEMOTE_AUDIENCE:
        // @ts-expect-error TS2345 - strict migration
        return `Demoted audience to \"${contentAudienceDisplayNames.get(data?.demoteToAudience)}\"`;
      case WorkflowEventTypes.CHANGE_OWNER:
        return `Modified owner to ${data?.newOwnerId}`;
      default:
        return '';
    }
  };
  const columnDefinitions = [
    {
      cell: (item: WorkflowEvent) =>
        item.userProfile
          ? displayAuthorInfo(item.userProfile?.name, item.userProfile?.organizationName)
          : item.createdBy,
      header: 'User',
      id: 'user',
      sortingField: 'createdBy',
    },
    {
      cell: (item: WorkflowEvent) => {
        const userTitle = item.userProfile
          ? getUserTitleFromProfile(
              item.userProfile,
              // @ts-expect-error TS2345 - strict migration
              owner?.userId,
              // @ts-expect-error TS18048 - strict migration
              coAuthors.map((ca) => ca.userId),
              // @ts-expect-error TS18048 - strict migration
              peerReviewers.map((pr) => pr.userId),
            )
          : null;
        // @ts-expect-error TS2345 - strict migration
        return item.userProfile ? userTitleDisplayNames.get(userTitle) : item.createdBy;
      },
      header: 'Role',
      id: 'role',
      sortingField: 'userProfile',
    },
    {
      cell: (item: WorkflowEvent) => item.createdAt.toLocaleString(DateTime.DATE_FULL),
      header: 'Date',
      id: 'date',
      sortingField: 'createdAt',
    },
    {
      cell: (item: WorkflowEvent) => workflowEventTypeDisplayNames.get(item.type),
      header: 'Status',
      id: 'status',
      sortingField: 'type',
    },
    {
      cell: (item: WorkflowEvent) => item.comment,
      header: 'Comment',
      id: 'comment',
      sortingField: 'comment',
    },
    {
      cell: (item: WorkflowEvent) => displayInfo(item),
      header: 'Info',
      id: 'info',
      sortingField: 'data',
    },
  ];

  if (isLoading) {
    return <LoadingIndicator />;
  }

  return (
    <Table
      {...collectionProps}
      columnDefinitions={columnDefinitions}
      items={arraySort(items as any[], 'createdAt', { reverse: true })}
      resizableColumns
      wrapLines
    />
  );
};

export default WorkflowHistoryEvents;
